import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { HelpAside } from '../components/SettingsAsides';
import { useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PageHeader, SidePanel } from '../components/forms';
import { Button, ErrorNote, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { chooseLocation, useLocations, type SalonLocation } from '../lib/location';

/** Salon → Locaties (shown in the hub with 2+ locations; the first extra one is added from Bedrijfsgegevens). */
export function LocationsPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const { nieuw } = useSearch({ from: '/s/$orgId/salon/locaties' });
  const t = useT();
  const locations = useLocations(orgId);
  const [open, setOpen] = useState<{ location?: SalonLocation } | null>(nieuw ? {} : null);

  return (
    <SettingsPage orgId={orgId} aside={<HelpAside slugs={['locaties']} />}>
      <PageHeader orgId={orgId} title={t('loc.title')} intro={t('loc.intro')} action={<Button onClick={() => setOpen({})}>+ {t('loc.add')}</Button>} />
      <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
        {locations.data?.map((l) => (
          <li key={l.id}>
            <button className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-subtle" onClick={() => setOpen({ location: l })}>
              <span>
                <span className="font-medium">{l.name}</span>
                <span className="block text-sm text-muted">{[[l.address.street, l.address.houseNumber].filter(Boolean).join(' '), l.address.city].filter(Boolean).join(', ') || '—'}</span>
              </span>
              <span aria-hidden className="text-muted">
                ›
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted">{t('loc.hoursHint')}</p>
      {open && <LocationPanel orgId={orgId} location={open.location} canRemove={(locations.data?.length ?? 0) > 1} onClose={() => setOpen(null)} />}
    </SettingsPage>
  );
}

function LocationPanel({ orgId, location, canRemove, onClose }: { orgId: string; location?: SalonLocation; canRemove: boolean; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [name, setName] = useState(location?.name ?? '');
  const [phone, setPhone] = useState(location?.phone ?? '');
  const [street, setStreet] = useState(location?.address.street ?? '');
  const [houseNumber, setHouseNumber] = useState(location?.address.houseNumber ?? '');
  const [postcode, setPostcode] = useState(location?.address.postcode ?? '');
  const [city, setCity] = useState(location?.address.city ?? '');
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['locations', orgId] });
    onClose();
  };
  const body = () => JSON.stringify({ name, phone, address: { street, houseNumber, postcode, city } });
  const save = useMutation({
    mutationFn: async () => {
      if (location) return api(`/orgs/${orgId}/locations/${location.id}`, { method: 'PUT', body: body() });
      const r = await api<{ location: SalonLocation }>(`/orgs/${orgId}/locations`, { method: 'POST', body: body() });
      chooseLocation(orgId, r.location.id);
      return r;
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api(`/orgs/${orgId}/locations/${location!.id}`, { method: 'DELETE' }), onSuccess: done });

  // Fill street and city from postcode + house number (PDOK), like Bedrijfsgegevens.
  useEffect(() => {
    const pc = postcode.replace(/\s/g, '').toUpperCase();
    if (!/^\d{4}[A-Z]{2}$/.test(pc) || !houseNumber.trim() || street) return;
    const id = window.setTimeout(() => {
      void api<{ address: { street: string; city: string } | null }>(`/address?${new URLSearchParams({ postcode: pc, number: houseNumber })}`).then((r) => {
        if (r.address) {
          setStreet(r.address.street);
          setCity(r.address.city);
        }
      });
    }, 400);
    return () => window.clearTimeout(id);
  }, [postcode, houseNumber, street]);

  const error = save.error ?? remove.error;
  return (
    <SidePanel
      open
      title={location ? location.name : t('loc.new')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={() => save.mutate()} disabled={save.isPending || name.trim().length < 2}>
            {t('common.save')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {location && canRemove && (
            <Button variant="ghost" className="ml-auto text-danger" onClick={() => window.confirm(t('loc.archiveConfirm')) && remove.mutate()}>
              {t('loc.archive')}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field id="loc-name" label={t('loc.name')} hint={t('loc.nameHint')} value={name} onChange={(e) => setName(e.target.value)} required />
        <div className="grid grid-cols-2 gap-3">
          <Field id="loc-postcode" label={t('loc.postcode')} value={postcode} onChange={(e) => setPostcode(e.target.value)} autoComplete="postal-code" />
          <Field id="loc-number" label={t('loc.houseNumber')} value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} />
        </div>
        <Field id="loc-street" label={t('loc.street')} value={street} onChange={(e) => setStreet(e.target.value)} autoComplete="address-line1" />
        <Field id="loc-city" label={t('loc.city')} value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" />
        <Field id="loc-phone" type="tel" label={t('loc.phone')} value={phone} onChange={(e) => setPhone(e.target.value)} />
        <ErrorNote message={error instanceof ApiError ? error.message : error ? t('error.generic') : null} />
      </div>
    </SidePanel>
  );
}
